import React from "react";

import icons from "assets/icons/symbol-defs.svg";
import polyPlusBadge from "assets/images/poly-plus-badge.svg";

const iconAliases: Record<string, string> = {
  ball: "connected",
  clear: "clear_x",
  server_icon_active: "server_active",
  server_icon_staged: "server_staged",
  x_circle: "clear_circle_outline"
};

interface PolyIconProps {
  icon: string; // the name of the icon as defined in symbol-defs.svg file
  title?: string; // optional - title to override the symbol-def title
  className?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  "data-testid"?: string;
}

/**
 * Render an icon using the approved Poly icon set.
 *
 * @example <PolyIcon icon="home" title="Home Page" />
 *
 * @help https://onecode.polycom-labs.com/UME/Poly-Icons/tree/master
 * @help https://plcmcdndev.blob.core.windows.net/assets/semantic/latest/examples/plcm/poly-icons.html
 *
 * @param {PolyIconProps} props
 *
 */
export const PolyIcon: React.FC<PolyIconProps> = props => {
  const {
    title,
    className,
    onClick,
    onMouseEnter,
    onMouseLeave,
    ...rest
  } = props;

  const icon = iconAliases[props.icon] || props.icon;
  const classes = ["poly-icon", icon, className || ""].join(" ");

  return (
    <svg
      className={classes}
      xmlnsXlink="http://www.w3.org/1999/xlink"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={rest["data-testid"]}
    >
      <use xlinkHref={`${icons}#${icon}`}>
        {title && <title>{title}</title>}
      </use>
    </svg>
  );
};

/**
 * Helper to just wrap a PolyIcon as a button
 */
export const PolyIconButton: React.FC<PolyIconProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    iconClassName?: string;
  }> = props => {
  const { icon, title, className, iconClassName, ...otherProps } = props;
  return (
    <button className={`btn-poly-icon ${className}`} {...otherProps}>
      <PolyIcon icon={icon} title={title} className={iconClassName} />
    </button>
  );
};

export const PolyPlusBadge: React.FC<{ className?: string }> = ({
  className
}) => {
  const classes = ["poly-plus-badge", className].filter(c => c).join(" ");
  return (
    <img
      className={classes}
      src={polyPlusBadge}
      alt="Poly+"
      data-testid="poly-plus-badge"
    />
  );
};

export default PolyIcon;
